import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DownloadSimple } from '@phosphor-icons/react';
import { downloadExport } from '@/lib/exporter';

export const MyStockPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      const res = await api.get(`/stock-ledger?pharmacy_id=${user.pharmacy_id}`);
      setStock(res.data || []);
    } catch (error) {
      console.error('Failed to fetch stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExpiryColor = (expiryMonth, expiryYear) => {
    const now = new Date();
    const expiry = new Date(expiryYear, expiryMonth - 1);
    const monthsDiff = (expiry.getFullYear() - now.getFullYear()) * 12 + (expiry.getMonth() - now.getMonth());

    if (monthsDiff <= 3) return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    if (monthsDiff <= 6) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
    if (monthsDiff <= 9) return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' };
    if (monthsDiff <= 12) return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
    return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
  };

  if (loading) return <div className="p-8">{t('common.loading')}</div>;

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
          {t('nav.myStock')}
        </h1>
        <Button data-testid="export-my-stock-btn" variant="outline" size="sm" onClick={() => downloadExport('/exports/stock', 'MyStock.xlsx')}>
          <DownloadSimple size={14} className="mr-2" /> Export
        </Button>
      </div>

      <div className="space-y-4">
        {stock.map((item, idx) => {
          const colors = getExpiryColor(item.expiry_month, item.expiry_year);
          return (
            <Card key={idx} data-testid={`stock-item-${idx}`} className={`p-4 ${colors.bg} border-l-4 ${colors.border}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg">{item.product_name}</h3>
                  <p className="text-sm text-slate-600">Batch: {item.batch_number}</p>
                  <p className={`text-sm font-semibold ${colors.text}`}>
                    Expiry: {item.expiry_month?.toString().padStart(2, '0')}/{item.expiry_year}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{item.quantity}</p>
                  <p className="text-sm text-slate-600">{item.unit_label || 'units'}</p>
                </div>
              </div>
            </Card>
          );
        })}
        {stock.length === 0 && (
          <p className="text-center text-slate-500 py-8">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
};